import I18n from 'i18n-js/index.js.erb';
import displayDatatablesPagination from '../shared/datatables_pagination';
import { withSaveState } from '../shared/datatables_state';
import SidePanel from './sidepanel';
import MobileDropdown from './mobile_dropdown';
import {
  overridePaginationClasses,
  customizeDOMPaginationClasses
} from '../../datatables/customization-v2';
import {
  setup as tableSetup,
  setupPaginationOnDOM,
  customizePageLength,
  defaultTableOptions,
  buildReportPrintButton,
  resolvePrintHeader
} from '../../datatables/table-setup';

const exportedContentFormat = {
  body(data, _row, _column, node) {
    return node.dataset.display || data;
  }
};

// The print-only Orders table is display:none on screen, so the export must NOT
// use the shared ":visible" column selector (it would match nothing).
const ordersExportOptions = {
  format: exportedContentFormat,
  modifier: { page: 'all', search: 'applied' },
  columns: ':not(.not-export)'
};

const setupExportButtons = (table) => {
  const tableButtons = new $.fn.dataTable.Buttons(table, {
    buttons: [
      // Shared report print template (heading, footer, "Event - Orders" filename);
      // title/subtitle/doc-title come from the print table's data-print-* attrs.
      buildReportPrintButton({ ...resolvePrintHeader(table), exportOptions: ordersExportOptions }),
      {
        extend: 'csv',
        className: 'js-csv-button',
        exportOptions: {
          format: exportedContentFormat,
          modifier: {
            page: 'all',
            search: 'applied'
          },
          columns: ':not(.not-export)'
        }
      },
      {
        extend: 'copyHtml5',
        className: 'js-copy-to-clipboard-button',
        title: null,
        exportOptions: {
          format: exportedContentFormat,
          modifier: {
            page: 'all',
            search: 'applied'
          },
          columns: ':not(.not-export)'
        }
      }
    ]
  });

  $('.js-print-export-print').click((event) => {
    event.preventDefault();
    table.button('.js-print-button').trigger();
  });

  $('.js-print-export-csv').click((event) => {
    event.preventDefault();
    table.button('.js-csv-button').trigger();
  });

  $('.js-print-export-clipboard').click((event) => {
    event.preventDefault();
    table.button('.js-copy-to-clipboard-button').trigger();
  });

  return tableButtons;
};

const OrdersTable = {
  start() {
    const $table = $('.orders-table:not(.dataTable)');
    if (!$table.length) {
      return;
    }
    const $printTable = $('.print-orders-table:not(.dataTable)');
    this.setupDataTable($table, $printTable);
  },

  setupDataTable($table, $printTable) {
    overridePaginationClasses();
    const printTableOptions = {
      dom: 't',
      paging: false,
      order: [[2, 'desc']],
      columnDefs: [
        { targets: [2, 5], searchable: false },
        { targets: '_all', searchable: true }
      ]
    };
    const { processing, serverSide, source, columns } = $table.data();
    const tableOptions = {
      ...defaultTableOptions,
      lengthMenu: [[10, 50, 100, -1], [10, 50, 100, 'All']],
      ajax: { url: source },
      pagingType: 'full_numbers',
      processing,
      serverSide,
      columns,
      order: [[2, 'desc']],
      dom: 'rtp',
      columnDefs: [
        { targets: [0], orderable: true, width: '7%' }
      ],
      drawCallback() {
        customizePageLength(this);
        customizeDOMPaginationClasses();
        displayDatatablesPagination(this);
        MobileDropdown.hookEvents($table);
      },
      initComplete: () => {
        setupPaginationOnDOM();
        this._initialSearch($table);
      },
      language: {
        ...defaultTableOptions.language,
        zeroRecords:
          '<span class="alert alert--info">Sorry, no matching Orders were found</span>',
        emptyTable: '<span class="alert alert--info">No Orders</span>'
      }
    };
    // Persist the Paid/Pending filter selection alongside DataTables' built-in
    // state (order, page, search). Listeners must be bound BEFORE .DataTable()
    // so the stateLoadParams event fired during init is caught.
    const selectedStatuses = new Set();
    $table.on('stateSaveParams.dt', (e, settings, data) => {
      data.selectedPaymentStatuses = Array.from(selectedStatuses);
    });
    $table.on('stateLoadParams.dt', (e, settings, data) => {
      if (!Array.isArray(data.selectedPaymentStatuses) || data.selectedPaymentStatuses.length === 0) {
        return;
      }
      selectedStatuses.clear();
      data.selectedPaymentStatuses.forEach((status) => selectedStatuses.add(String(status)));
      // Restore the action-bar checkboxes without firing change (no reload per box).
      selectedStatuses.forEach((status) => {
        $(`.js-orders-filter-check[data-payment-status="${status}"]`).prop('checked', true);
      });
      this._updateOrdersFilterUI(selectedStatuses.size);
    });

    const tableOptionsWithSaveState = withSaveState($table, tableOptions);
    const table = $table.DataTable(tableOptionsWithSaveState);
    const printTable = $printTable.DataTable(printTableOptions);

    // Inject the selected Paid/Pending statuses into every server-side request.
    $table.on('preXhr.dt', (e, _settings, data) => {
      const statuses = $('.js-orders-filter-check:checked')
        .map((_, el) => String($(el).data('payment-status')))
        .get();
      if (statuses.length > 0) {
        data.payment_statuses = statuses;
      } else {
        delete data.payment_statuses;
      }
    });

    this._watchTableChanges(table, printTable);
    this._watchOrdersFilter(table, selectedStatuses);
    setupExportButtons(printTable);
    table.on('draw', this._updateItemsCounter.bind(this));
    $table.find('tbody').on('click', '.js-expand-order-items', this._toggleOrderItemsDetails);
    $table.find('tbody').on('click', '[data-sidepanel]', SidePanel.loadAndShow.bind(SidePanel));
    tableSetup(table);
  },

  _watchOrdersFilter(table, selectedStatuses) {
    // Keep the dropdown open while toggling its options.
    $('.js-orders-filter-dropdown .dropdown__list').on('click', (e) => {
      e.stopPropagation();
    });

    $('.js-orders-filter-check').on('change', (e) => {
      const status = String($(e.currentTarget).data('payment-status'));
      if (e.currentTarget.checked) {
        selectedStatuses.add(status);
      } else {
        selectedStatuses.delete(status);
      }
      this._updateOrdersFilterUI(selectedStatuses.size);
      table.state.save();
      table.ajax.reload();
    });

    $('.js-orders-filter-clear').on('click', (e) => {
      e.preventDefault();
      selectedStatuses.clear();
      $('.js-orders-filter-check').prop('checked', false);
      this._updateOrdersFilterUI(0);
      table.state.save();
      table.ajax.reload();
    });
  },

  _updateOrdersFilterUI(count) {
    $('.js-orders-filter-counter').text(count).toggleClass('is-hidden', count === 0);
    $('.js-orders-filter-clear').toggleClass('is-hidden', count === 0);
  },

  _initialSearch($table) {
    setTimeout(() => {
      const searchValue = $table.closest('.js-orders-table-container').find('.js-search-input').val() || '';
      if (searchValue !== '') {
        const table = $table.dataTable().api();
        table.search(searchValue).draw();
      }
    }, 10);
  },

  _updateItemsCounter(event) {
    const $table = $(event.currentTarget);
    const tableApi = $table.dataTable().api();
    const { recordsTotal, recordsDisplay } = tableApi.page.info();
    const $totalItems = $table.closest('.js-orders-table-container').find('[data-total-items]');
    const totalItemsLabel = I18n.t('my_events.orders.orders_table.items', { count: recordsTotal });
    if (recordsDisplay === recordsTotal) {
      $totalItems.text(totalItemsLabel);
    } else {
      $totalItems.text(`${recordsDisplay} of ${totalItemsLabel}`);
    }
  },

  _watchTableChanges(table, printTable) {
    table.on('draw.dt', () => {
      const searchValue = $('.js-search-input').val();
      printTable.order(table.order()).search(searchValue).draw();
    });
  },

  _toggleOrderItemsDetails(event) {
    event.preventDefault();

    const $td = $(event.currentTarget);
    const $tr = $td.closest('tr');
    const table = $td.closest('table').DataTable();
    const row = table.row($tr);
    const $arrowUp = $td.find('.js-arrow-up');
    const $arrowDown = $td.find('.js-arrow-down');

    if ($arrowDown.hasClass('is-hidden')) {
      $arrowDown.removeClass('is-hidden');
      $arrowUp.addClass('is-hidden');
      row.child.hide();
    } else {
      $arrowDown.addClass('is-hidden');
      $arrowUp.removeClass('is-hidden');
      const content = $td.find('.js-order-items-table').html();
      row.child(content).show();
      const $childRow = $(row.child());
      $childRow.find('td').addClass('table__data table__data--merged');
      $childRow.addClass('table__row table__row--merged');
    }
  }
};

export default OrdersTable;
